// Analytics bridge — ported from eb-ui creator-collections-listing `src/_shared/analytics.ts`.
//
// `@eventbrite/legacy-infra.site-analytics` (GA) is a documented GA-ghost drop
// (GA → Heap → PicoX). The KEYS still flow through the app (trackEvent is called
// from every interactive surface) and the DISPATCH now targets PicoX via
// `usePicoXTrack()` (the bsp-sdk hook the legacy `BspAppRoot` enabled —
// `site-structure-bsp`'s `BspAppRoot` booted the same PicoX client on the Django
// page). The hook must be used INSIDE `<BspProvider>` (mounted app-wide in
// `_app`), so the bridge lives in a context-consumer component; `trackEvent`
// (the exported call-site API, unchanged) dispatches through it.
//
// Consent: BspProvider's PicoX processor re-evaluates window.airgap per-event
// (Transcend gate, fail-open if not ready) — same undercount semantics Heap had.
// CloudWatch/API GW metrics remain the consent-independent volume SSOT.
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import type { JsonObject } from '@eventbrite/picox-web';
import React from 'react';

import type { ServerData } from '@/types';

export enum AnalyticsCategory {
    EventCard = 'eventcard',
    Collections = 'creator-collections',
    Create = 'creator-collections-create',
    Listing = 'creator-collections-listing',
    RelatedEvents = 'related-events',
}

export enum AnalyticsAction {
    AutoCollectionInitiate = 'AutoCollectionInitiate',
    CollectionPromote = 'CollectionPromote',
    CollectionView = 'CollectionView',
    CopyURL = 'CopyURL',
    DevicePreview = 'DevicePreview',
    EditCollection = 'EditCollection',
    EventsShowMore = 'EventsShowMore',
    EventsTabClick = 'EventsTabClick',
    OrganizerName = 'OrganizerName',
    PreviewPage = 'PreviewPage',
}

/** GA init (site-analytics) — dropped; no-op kept so the bootstrap call site survives. */
export const initAnalytics = (_props: ServerData): void => {
    // (GA initDimensionsFromState / trackInitialPageView dropped — see module comment.)
};

export interface TrackEventPayload {
    action: AnalyticsAction;
    label: string;
    category: AnalyticsCategory;
    dimensions?: Record<string, unknown>;
}

type PicoXTrackFn = (event: string, props?: Record<string, unknown>) => void;

// bsp-sdk's track takes a strict JSON object; cast the payload (call sites pass
// plain string/number/bool fields from the legacy GA shape).
const toPicoXTrackFn = (track: (event: string, payload?: JsonObject) => void): PicoXTrackFn =>
    (event, props) => track(event, props as JsonObject | undefined);

// Set by <AnalyticsBridge/> (inside <BspProvider>) on mount; module-level so
// the exported `trackEvent` keeps its legacy signature (call sites unchanged).
let picoxDispatch: PicoXTrackFn | null = null;

/**
 * Mount inside `<BspProvider>` (e.g. once in the page tree) to arm the
 * `trackEvent` dispatch. No-op-safe: until armed (or outside a BSP tree,
 * e.g. fixtures mode) events are dropped exactly like the GA no-op was.
 */
export const AnalyticsBridge = {
    arm(dispatch: PicoXTrackFn | null) {
        picoxDispatch = dispatch;
    },
};

/**
 * Dispatch an analytics event. Fires a PicoX event keyed
 * `<category>:<action>` with the label + dimensions as payload properties
 * (the legacy GA shape: one category, one action, one label). No-op until
 * `<AnalyticsBridge/>` arms it inside `<BspProvider>`.
 */
export const trackEvent = (payload: TrackEventPayload): void => {
    if (!picoxDispatch) {
        return;
    }
    picoxDispatch(`${payload.category}:${payload.action}`, {
        label: payload.label,
        ...(payload.dimensions ?? {}),
    });
};

/**
 * React component form — render ONCE inside `<BspProvider>` (see `_app`):
 * arms the module dispatch with the live `usePicoXTrack()` function and
 * disarms on unmount. Kept here so the analytics module stays the single
 * seam (call sites never import bsp-sdk directly).
 */
export const AnalyticsBridgeMount: React.FC = () => {
    const track = toPicoXTrackFn(usePicoXTrack());
    React.useEffect(() => {
        AnalyticsBridge.arm(track);
        return () => AnalyticsBridge.arm(null);
    }, [track]);
    return null;
};
